import matrix from './featureFlags.matrix.json'

/**
 * Canonical build environment.
 *
 * The value is injected at build time by webpack's DefinePlugin - see the
 * `NODE_ENV` const at the top of each webpack/webpack.<env>.js. Note that the
 * webpack CLI env name and NODE_ENV are NOT the same string: `--env env=prod`
 * produces NODE_ENV === 'production'.
 *
 * This is the canonical env symbol for the app. src/environment/apis.config.ts
 * predates it and re-derives the same thing with nested ternaries; new code
 * should import from here.
 */
export type AppEnv = 'local' | 'dev' | 'joint' | 'staging' | 'production'

/**
 * Kept as an explicit tuple rather than being derived from the JSON: TypeScript
 * widens arrays in JSON modules to `string[]`, so `typeof matrix.envs[number]`
 * would be plain `string` and buy us nothing. The runtime check below is what
 * actually keeps the two lists honest.
 */
const APP_ENVS: readonly AppEnv[] = [
  'local',
  'dev',
  'joint',
  'staging',
  'production',
]

/*
 * IMPORTANT: `process.env.NODE_ENV` must be written out as this exact member
 * expression. DefinePlugin substitutes the literal source text, so
 * `const { NODE_ENV } = process.env` is NOT substituted and would throw
 * "process is not defined" in the browser.
 */
const RAW_NODE_ENV: string = process.env.NODE_ENV as string

const KNOWN = new Set<string>(APP_ENVS)

/** False when NODE_ENV is something we do not recognise - e.g. 'test' under jest. */
export const IS_KNOWN_ENV: boolean = KNOWN.has(RAW_NODE_ENV)

/**
 * Unknown env falls back rather than throwing. `npm test` runs jest through
 * babel-jest with no DefinePlugin, so NODE_ENV is 'test'; throwing at module
 * scope would break the suite. This also matches apis.config.ts, whose ternary
 * chains all fall through to their LOCAL branch.
 */
export const ENV: AppEnv = IS_KNOWN_ENV
  ? (RAW_NODE_ENV as AppEnv)
  : (matrix.fallbackEnv as AppEnv)

export const IS_PRODUCTION: boolean = ENV === 'production'

if (!IS_PRODUCTION) {
  const jsonEnvs = matrix.envs as string[]
  const drifted =
    jsonEnvs.length !== APP_ENVS.length ||
    jsonEnvs.some((e, i) => e !== APP_ENVS[i])
  if (drifted) {
    // eslint-disable-next-line no-console
    console.warn(
      `[feature-flags] env list drift: featureFlags.matrix.json has ` +
        `[${jsonEnvs.join(', ')}] but env.ts declares [${APP_ENVS.join(
          ', '
        )}]. ` +
        `Update AppEnv and APP_ENVS in src/environment/env.ts.`
    )
  }
  if (!IS_KNOWN_ENV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[feature-flags] unknown NODE_ENV "${RAW_NODE_ENV}", falling back to "${ENV}".`
    )
  }
}
